import React, { useContext, useMemo } from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import { NextPlanOutlined } from "@mui/icons-material";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import jwt_decode from "jwt-decode";
import CloudProviderMark from "../../../components/Common/CloudProviderMark";
import { useAccounts } from "../../../components/Common/HelperComponents/GetDataFromStore";
import {
  CloudProviderContext,
  getAccountCloudType,
} from "../../../CloudProviderContext";

/** Matches FinOps / cost value green on dashboard cards */
const COUNT_COLOR = "#228B22";
const INACTIVE_COLOR = "#6c757d";

/**
 * Compact dashboard card: account counts for the selected cloud provider.
 * Static (not draggable). Click / go-to opens Configuration → Accounts.
 */
const AccountsCard = () => {
  const theme = useTheme();
  const history = useHistory();
  const { selectedProvider } = useContext(CloudProviderContext);
  const accounts = useAccounts();

  const { totalCount, activeCount, inactiveCount } = useMemo(() => {
    let username = null;
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      try {
        username = jwt_decode(JSON.parse(authUser).idToken).email;
      } catch (error) {
        console.error("Error decoding token:", error);
        return { totalCount: 0, activeCount: 0, inactiveCount: 0 };
      }
    }

    if (!accounts || !username) {
      return { totalCount: 0, activeCount: 0, inactiveCount: 0 };
    }

    const providerAccounts = accounts.filter(
      (acct) =>
        !acct.accountNumber.includes("INIT00000001") &&
        !acct.accountNumber.includes("TEST") &&
        getAccountCloudType(acct) === selectedProvider &&
        acct.listusr?.some((user) => user.userName === username)
    );

    const active = providerAccounts.filter(
      (acct) => acct.status === "ACTIVE"
    ).length;
    const inactive = providerAccounts.filter(
      (acct) => acct.status === "INACTIVE"
    ).length;

    return {
      activeCount: active,
      inactiveCount: inactive,
      totalCount: active + inactive,
    };
  }, [accounts, selectedProvider]);

  const handleGoTo = () => {
    history.push("/configuration/accounts");
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label="Number of Accounts — open Configuration"
      onClick={handleGoTo}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleGoTo();
        }
      }}
      sx={{
        backgroundColor: theme.palette.background.default,
        borderRadius: "8px",
        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
        padding: "8px 12px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "box-shadow 0.15s ease",
        "&:hover": {
          boxShadow: "0 0 14px rgba(0,0,0,0.14)",
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        sx={{
          mb: 0.5,
          paddingBottom: "2px",
          borderBottom: `1px solid`,
        }}
      >
        <Box display="flex" alignItems="center" minWidth={0} flex={1}>
          <CloudProviderMark />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              color: "text.primary",
              fontSize: "0.875rem",
              lineHeight: 1.2,
            }}
          >
            Number of Accounts
          </Typography>
        </Box>
        <Tooltip title="Open Configuration">
          <IconButton
            aria-label="go-to-configuration"
            size="small"
            sx={{
              p: 0,
              ml: 0.5,
              color: "text.secondary",
              transform: "rotate(-45deg)",
              flexShrink: 0,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleGoTo();
            }}
          >
            <NextPlanOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
          width: "100%",
          gap: 0.75,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: COUNT_COLOR,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1.1,
              fontSize: { xs: "1.5rem", sm: "1.75rem" },
            }}
          >
            {totalCount}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.7rem",
              lineHeight: 1.2,
            }}
          >
            Total accounts
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: "6px",
              backgroundColor: "rgba(34, 139, 34, 0.12)",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                color: COUNT_COLOR,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              Active {activeCount}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: "6px",
              backgroundColor: "rgba(108, 117, 125, 0.12)",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                color: INACTIVE_COLOR,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              Inactive {inactiveCount}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AccountsCard;
